import { ChangeEvent, useCallback, useMemo, useState } from "react";

import instance from "../../../api/instance";
import endpoints from "../../../api/endpoints";
import { formatUnix } from "src/utils/date";
import parseNumber from "../../../utils/parse-number";
import { BetList } from "../../../types";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

type ActiveTab = "sports" | "casino";
type BetStatus = "matchbet" | "deletebet";
type BetTypeFilter = "all" | "back" | "lay";

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 125, 150];

const CurrentBetsPage = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>("sports");
  const [betStatus, setBetStatus] = useState<BetStatus>("matchbet");
  const [betTypeFilter, setBetTypeFilter] = useState<BetTypeFilter>("all");

  const [betList, setBetList] = useState<BetList[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useSyncGlobalPageLoader(loading);

  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
    setBetList([]);
    setTotalCount(0);
    setCurrentPage(1);
    setSearchText("");
  };

  const handleLoad = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        game_type: activeTab,
        bet_type: betStatus === "matchbet" ? "matched" : "un-matched",
        bet_status: "unsettle",
        limit: pageSize,
        offset: 0,
      };
      const searchQuery = searchText.trim();
      if (betTypeFilter !== "all") {
        params.type = betTypeFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const { data } = await instance.get(endpoints.BETLIST, { params });
      const list: BetList[] = Array.isArray(data?.data?.betList) ? data.data.betList : [];
      setBetList(list);
      setTotalCount(data?.data?.count ?? list.length);
      setCurrentPage(1);
    } catch {
      setBetList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, betStatus, betTypeFilter, pageSize, searchText]);

  const filteredList = useMemo(() => {
    if (!searchText.trim()) return betList;
    const q = searchText.toLowerCase();
    return betList.filter((row) =>
      [row.event_name, row.user_name, row.market_name, row.runner_name, row.type].some((field) =>
        field?.toLowerCase().includes(q)
      )
    );
  }, [betList, searchText]);

  const totalRecords = filteredList.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const pagedList = filteredList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  const totalAmount = useMemo(
    () => filteredList.reduce((sum, row) => sum + (parseNumber({ value: row.bet_amount, type: "float" }) as number), 0),
    [filteredList]
  );

  return (
    <div className="listing-grid report-wrapper">
      {/* Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Current Bets</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      {/* Tabs: Sports / Casino */}
      <div className="casino-report-tabs">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              type="button"
              data-toggle="tab"
              className={`nav-link ${activeTab === "sports" ? "active" : ""}`}
              onClick={() => handleTabChange("sports")}
            >
              Sports
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              data-toggle="tab"
              className={`nav-link ${activeTab === "casino" ? "active" : ""}`}
              onClick={() => handleTabChange("casino")}
            >
              Casino
            </button>
          </li>
        </ul>
      </div>

      {/* Card */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Filters */}
              <div className="report-form mb-3 row align-items-center modal-chekbox">
                {/* Matched / Deleted */}

                {activeTab !== "casino" && (
                  <div className="col-12">
                    <div className="custom-control custom-radio custom-control-inline">
                      <input
                        type="radio"
                        id="customRadio"
                        name="example"
                        value="matchbet"
                        checked={betStatus === "matchbet"}
                        className="custom-control-input"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setBetStatus(e.target.value as BetStatus)}
                      />
                      <label htmlFor="customRadio" className="custom-control-label">
                        Matched
                      </label>
                    </div>
                    <div className="custom-control custom-radio custom-control-inline">
                      <input
                        type="radio"
                        id="customRadio2"
                        name="example"
                        value="deletebet"
                        checked={betStatus === "deletebet"}
                        className="custom-control-input"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setBetStatus(e.target.value as BetStatus)}
                      />
                      <label htmlFor="customRadio2" className="custom-control-label">
                        Deleted
                      </label>
                    </div>
                  </div>
                 )}
                
                {/* All / Back / Lay + Load */}
                <div className="col-6">
                  <div className="custom-control custom-radio custom-control-inline pl-0">
                    <div className="custom-control custom-radio custom-control-inline">
                      <input
                        type="radio"
                        id="soda-all"
                        name="bettype"
                        value="all"
                        checked={betTypeFilter === "all"}
                        className="custom-control-input"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setBetTypeFilter(e.target.value as BetTypeFilter)
                        }
                      />
                      <label htmlFor="soda-all" className="custom-control-label">
                        All
                      </label>
                    </div>
                    <div className="custom-control custom-radio custom-control-inline">
                      <input
                        type="radio"
                        id="soda-back"
                        name="bettype"
                        value="back"
                        checked={betTypeFilter === "back"}
                        className="custom-control-input"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setBetTypeFilter(e.target.value as BetTypeFilter)
                        }
                      />
                      <label htmlFor="soda-back" className="custom-control-label">
                        Back
                      </label>
                    </div>
                    <div className="custom-control custom-radio custom-control-inline">
                      <input
                        type="radio"
                        id="soda-lay"
                        name="bettype"
                        value="lay"
                        checked={betTypeFilter === "lay"}
                        className="custom-control-input"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setBetTypeFilter(e.target.value as BetTypeFilter)
                        }
                      />
                      <label htmlFor="soda-lay" className="custom-control-label">
                        Lay
                      </label>
                    </div>
                  </div>
                  <div className="custom-control-inline">
                    <button
                      title="Refresh Data"
                      type="button"
                      className="btn mr-2 btn-primary"
                      onClick={handleLoad}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Load"}
                    </button>
                  </div>
                </div>

                {/* Summary */}
                <div className="col-6 text-right">
                  <div className="custom-control-inlinemr-0 mt-1">
                    <h5>
                      Total Soda: <span className="mr-2">{filteredList.length}</span> Total Amount:{" "}
                      <span>{totalAmount.toFixed(2)}</span>
                    </h5>
                  </div>
                </div>
              </div>

              {/* Show entries + Search */}
              <div className="row w-100">
                <div className="col-6">
                  <div id="tickets-table_length" className="dataTables_length">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Show&nbsp;
                      <select
                        className="custom-select custom-select-sm"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      &nbsp;entries
                    </label>
                  </div>
                </div>
                <div className="col-6 text-right">
                  <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Search:
                      <input
                        type="search"
                        placeholder="Search..."
                        className="form-control ml-1 form-control"
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="table-responsive mb-0 report-table">
                <div className="table no-footer table-responsive-sm">
                  <table
                    id="eventsListTbl"
                    role="table"
                    aria-busy="false"
                    aria-colcount={8}
                    className="table b-table table-striped table-bordered"
                  >
                    <colgroup>
                      <col style={{ width: "auto" }} />
                      <col style={{ width: "200px" }} />
                      <col style={{ width: "auto" }} />
                      <col style={{ width: "auto" }} />
                      <col style={{ width: "auto" }} />
                      <col style={{ width: "auto" }} />
                      <col style={{ width: "auto" }} />
                      <col style={{ width: "auto" }} />
                    </colgroup>
                    <thead>
                      <tr role="row">
                        <th role="columnheader" scope="col" aria-colindex={1} className="position-relative">
                          <div>Event Type</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={2} className="position-relative">
                          <div>Event Name</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={3} className="position-relative">
                          <div>User Name</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={4} className="position-relative">
                          <div>M Name</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={5} className="position-relative">
                          <div>Nation</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={6} className="position-relative text-right">
                          <div>U Rate</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={7} className="position-relative text-right">
                          <div>Amount</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={8} className="position-relative">
                          <div>Place Date</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedList.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center">
                            {loading ? "Loading..." : "No data available"}
                          </td>
                        </tr>
                      ) : (
                        pagedList.map((row, idx) => (
                          <tr
                            key={row.bet_id ?? idx}
                            role="row"
                            aria-rowindex={idx + 1}
                            className={row.type?.toLowerCase() === "back" ? "back-border" : "lay-border"}
                          >
                            <td aria-colindex={1}>{(row as any).game_name ?? row.type}</td>
                            <td aria-colindex={2}>{row.event_name}</td>
                            <td aria-colindex={3}>{row.user_name}</td>
                            <td aria-colindex={4}>{row.market_name}</td>
                            <td aria-colindex={5}>
                              <p className="mb-0">{row.runner_name}</p>
                            </td>
                            <td aria-colindex={6}>
                              <p className="text-right mb-0">{row.requested_odds}</p>
                            </td>
                            <td aria-colindex={7}>
                              <p className="text-right mb-0">
                                {parseNumber({ value: row.bet_amount, type: "float" }).toFixed(2)}
                              </p>
                            </td>
                            <td aria-colindex={8}>
                              <div>{formatUnix({ unix: row.created_at, format: "DD/MM/YYYY HH:mm:ss" })}</div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="row pt-3">
                <div className="col">
                  <div className="dataTables_paginate paging_simple_numbers float-right">
                    <ul className="pagination pagination-rounded mb-0">
                      <ul
                        role="menubar"
                        aria-disabled="false"
                        aria-label="Pagination"
                        className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
                      >
                        <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to first page"
                            disabled={isFirstPage}
                            tabIndex={isFirstPage ? -1 : 0}
                            className="page-link"
                            onClick={() => setCurrentPage(1)}
                          >
                            &laquo;
                          </button>
                        </li>
                        <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to previous page"
                            disabled={isFirstPage}
                            tabIndex={isFirstPage ? -1 : 0}
                            className="page-link"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          >
                            &lsaquo;
                          </button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <li
                            key={page}
                            role="presentation"
                            className={`page-item ${currentPage === page ? "active" : ""}`}
                          >
                            <button
                              role="menuitemradio"
                              type="button"
                              aria-label={`Go to page ${page}`}
                              aria-checked={currentPage === page}
                              aria-posinset={page}
                              aria-setsize={totalPages}
                              tabIndex={currentPage === page ? 0 : -1}
                              className="page-link"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}
                        <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to next page"
                            disabled={isLastPage}
                            tabIndex={isLastPage ? -1 : 0}
                            className="page-link"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          >
                            &rsaquo;
                          </button>
                        </li>
                        <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to last page"
                            disabled={isLastPage}
                            tabIndex={isLastPage ? -1 : 0}
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentBetsPage;
